import { useNavigate } from "react-router-dom";
import { useAuth } from "../modules/auth/AuthContext";
import { useThemeMode } from "../hooks/useThemeMode";

function SettingsIcon({ name }: { name: "gear" | "moon" | "sun" | "logout" | "shield" | "info" }) {
  const paths = {
    gear: <><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.1a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
    moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></>,
    shield: <path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" />,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

export function SettingsPage() {
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  function handleSignOut() {
    signOut();
    navigate("/login");
  }

  return (
    <section className="settings-page-v2">
      <header className="settings-v2-hero">
        <span className="settings-v2-hero-icon"><SettingsIcon name="gear" /></span>
        <div>
          <h1>Configuración</h1>
          <p>Ajusta la apariencia y gestiona tu sesión en el portal.</p>
        </div>
      </header>

      <article className="settings-v2-card">
        <h2><SettingsIcon name={isDark ? "moon" : "sun"} /> Apariencia</h2>
        <div className="settings-v2-row">
          <div className="settings-v2-row-icon settings-v2-row-icon--purple">
            <SettingsIcon name={isDark ? "moon" : "sun"} />
          </div>
          <div className="settings-v2-row-body">
            <strong>Modo oscuro</strong>
            <p>Cambia el tema del portal entre claro y oscuro. Tu elección se recuerda en este dispositivo.</p>
          </div>
          <button
            type="button"
            className={`settings-v2-toggle ${isDark ? "settings-v2-toggle--on" : ""}`}
            aria-label={isDark ? "Desactivar modo oscuro" : "Activar modo oscuro"}
            aria-pressed={isDark}
            onClick={toggleTheme}
          >
            <i />
          </button>
        </div>
      </article>

      <article className="settings-v2-card">
        <h2><SettingsIcon name="shield" /> Sesión</h2>
        <div className="settings-v2-row">
          <div className="settings-v2-row-icon settings-v2-row-icon--red">
            <SettingsIcon name="logout" />
          </div>
          <div className="settings-v2-row-body">
            <strong>Cerrar sesión</strong>
            <p>Saldrás del portal en este dispositivo. Tu información permanece guardada.</p>
          </div>
          <button type="button" className="settings-v2-signout" onClick={handleSignOut}>
            <SettingsIcon name="logout" /> Cerrar sesión
          </button>
        </div>
      </article>

      <article className="settings-v2-card settings-v2-card--info">
        <div className="settings-v2-info-icon"><SettingsIcon name="info" /></div>
        <div>
          <strong>¿Necesitas cambiar tus datos o contraseña?</strong>
          <p>El cambio de correo, nombre o contraseña lo realiza el administrador del sistema. Escríbele para coordinarlo.</p>
        </div>
      </article>
    </section>
  );
}
